import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import AppLayout from '../components/AppLayout';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Plus, Search, Phone, Mail, Linkedin, 
  Calendar, RotateCcw, Eye,
  ChevronDown, ChevronUp, CheckSquare, Square,
  X, Building2, Users, CalendarIcon
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import VideoHelpButton from '../components/VideoHelpButton';
import { sanitizeHTML } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const taskTypeIcons = {
  call: Phone,
  email: Mail,
  linkedin: Linkedin,
  meeting: Calendar,
  follow_up: RotateCcw,
  review: Eye,
  other: CheckSquare
};

const taskTypeLabels = {
  call: 'Call',
  email: 'Email',
  linkedin: 'LinkedIn',
  meeting: 'Meeting',
  follow_up: 'Follow-up',
  review: 'Review',
  other: 'Other'
};

const priorityConfig = {
  high: { label: 'High', color: 'red', icon: '🔥' },
  medium: { label: 'Medium', color: 'yellow', icon: '⚡' },
  low: { label: 'Low', color: 'blue', icon: '🔵' }
};

// TaskCard Component
const TaskCard = ({ task, isDark, today, onComplete, onSnooze, onEdit, onRetire, onSendEmail, isSelected, onToggleSelect }) => {
  const TypeIcon = taskTypeIcons[task.task_type] || CheckSquare;
  const priority = priorityConfig[task.priority] || priorityConfig.medium;
  
  const handleRetire = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRetire(task.task_id);
  };

  const handleCardClick = (e) => {
    // Don't open edit if clicking on buttons or checkboxes
    if (e.target.closest('button') || e.target.closest('a')) {
      return;
    }
    onEdit(task);
  };
  
  return (
    <div 
      onClick={handleCardClick}
      className={`${isDark ? 'bg-[#111] border-white/10 hover:border-white/20' : 'bg-white border-gray-200 hover:border-gray-300'} ${isSelected ? 'ring-2 ring-blue-500' : ''} border rounded-sm p-4 transition-colors cursor-pointer`}
    >
      <div className="flex items-start gap-3">
        {/* Selection checkbox */}
        {task.status === 'pending' && onToggleSelect && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleSelect(task.task_id); }}
            className={`mt-0.5 ${isSelected ? 'text-blue-500' : isDark ? 'text-gray-600 hover:text-gray-400' : 'text-gray-300 hover:text-gray-500'}`}
          >
            {isSelected ? (
              <CheckSquare className="w-5 h-5" />
            ) : (
              <Square className="w-5 h-5" />
            )}
          </button>
        )}
        
        <button
          onClick={() => onComplete(task.task_id)}
          className={`mt-0.5 ${isDark ? 'text-gray-500 hover:text-[#A2FF00]' : 'text-gray-400 hover:text-green-500'}`}
        >
          {task.status === 'completed' ? (
            <CheckSquare className="w-5 h-5 text-[#A2FF00]" />
          ) : (
            <Square className="w-5 h-5" />
          )}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className={`font-medium ${task.status === 'completed' ? 'line-through opacity-60' : ''} ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {task.title}
              </h3>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {task.linked_card_name && (
                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    🏢 {task.linked_card_name}
                  </span>
                )}
                {task.due_date && (
                  <span className={`text-xs ${task.days_overdue > 0 ? 'text-red-400' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {task.days_overdue > 0 ? `${task.days_overdue}d overdue` : task.due_date === today ? `Due ${task.due_time || 'today'}` : `Due ${task.due_date}`}
                  </span>
                )}
                {task.snooze_count > 0 && (
                  <span className={`text-xs ${isDark ? 'text-orange-400' : 'text-orange-500'}`}>
                    Snoozed {task.snooze_count}x
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                priority.color === 'red' ? isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600' :
                priority.color === 'yellow' ? isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600' :
                isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
              }`}>
                {priority.icon} {priority.label}
              </span>
              <span className={`p-1.5 rounded ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <TypeIcon className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </span>
            </div>
          </div>
          
          {task.checklist && task.checklist.length > 0 && (
            <div className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              ✓ {task.checklist.filter(c => c.completed).length}/{task.checklist.length} items
            </div>
          )}
          
          {task.status !== 'completed' && (
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => onComplete(task.task_id)}
                className={`px-3 py-1.5 text-xs font-medium rounded ${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00] hover:bg-[#A2FF00]/30' : 'bg-green-100 text-green-600 hover:bg-green-200'}`}
              >
                ✓ Complete
              </button>
              <button
                onClick={() => onSnooze(task.task_id)}
                disabled={task.snooze_count >= 3}
                className={`px-3 py-1.5 text-xs font-medium rounded ${isDark ? 'bg-white/10 text-gray-300 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                ⏰ Snooze
              </button>
              <button
                onClick={() => onEdit(task)}
                className={`px-3 py-1.5 text-xs font-medium rounded ${isDark ? 'bg-white/10 text-gray-300 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                ✏️ Edit
              </button>
              <button
                onClick={handleRetire}
                className={`px-3 py-1.5 text-xs font-medium rounded ${isDark ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-500 hover:bg-red-100'}`}
              >
                🗑️ Retire
              </button>
              
              {task.task_type === 'linkedin' && task.linkedin_url && (
                <a
                  href={task.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`px-3 py-1.5 text-xs font-medium rounded ${isDark ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}
                >
                  🔗 Open LinkedIn
                </a>
              )}
              
              {task.task_type === 'payment_request' && task.proposal_id && onSendEmail && (
                <button
                  onClick={(e) => { e.stopPropagation(); onSendEmail(task); }}
                  className={`px-3 py-1.5 text-xs font-medium rounded ${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00] hover:bg-[#A2FF00]/30' : 'bg-green-100 text-green-600 hover:bg-green-200'}`}
                >
                  ✉️ Send Email
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// TaskSection Component
const TaskSection = ({ title, tasks, color, sectionKey, expanded, onToggle, isDark, today, onComplete, onSnooze, onEdit, onRetire, onSendEmail, selectedTasks, onToggleSelect }) => {
  if (tasks.length === 0) return null;
  
  return (
    <div className="mb-6">
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}
      >
        <span className={`w-3 h-3 rounded-full ${color}`} />
        <span className="font-bold">{title}</span>
        <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({tasks.length})</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="space-y-3">
          {tasks.map(task => (
            <TaskCard 
              key={task.task_id} 
              task={task} 
              isDark={isDark}
              today={today}
              onComplete={onComplete}
              onSnooze={onSnooze}
              onEdit={onEdit}
              onRetire={onRetire}
              onSendEmail={onSendEmail}
              isSelected={selectedTasks?.includes(task.task_id)}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// TaskModal Component with fuzzy company search
const TaskModal = ({ task, users, currentUserId, isDark, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: task?.title || '',
    task_type: task?.task_type || 'other',
    priority: task?.priority || 'medium',
    description: task?.description || '',
    assigned_to_id: task?.assigned_to_id || currentUserId || '',
    due_date: task?.due_date || '',
    due_time: task?.due_time || '',
    linked_card_id: task?.linked_card_id || '',
    checklist: task?.checklist || [],
    linkedin_url: task?.linkedin_url || '',
    linkedin_message_template: task?.linkedin_message_template || ''
  });
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Company search state
  const [companySearch, setCompanySearch] = useState(task?.linked_card_name || '');
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Fetch companies with fuzzy search
  const searchCompanies = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setCompanies([]);
      return;
    }
    
    setLoadingCompanies(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards?search=${encodeURIComponent(searchTerm)}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Fuzzy filter on client side for better matching
        const filtered = data.filter(card => 
          card.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
        ).slice(0, 10);
        setCompanies(filtered);
      }
    } catch (error) {
      console.error('Failed to search companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Handle company name input with debounced search
  const handleCompanySearchChange = (value) => {
    setCompanySearch(value);
    if (formData.linked_card_id) {
      setFormData(prev => ({ ...prev, linked_card_id: '' }));
    }
    // Debounce the search
    const timeoutId = setTimeout(() => searchCompanies(value), 300);
    return () => clearTimeout(timeoutId);
  };

  const selectCompany = (card) => {
    setFormData(prev => ({ ...prev, linked_card_id: card.card_id }));
    setCompanySearch(card.company_name);
    setCompanies([]);
  };

  const clearCompany = () => {
    setFormData(prev => ({ ...prev, linked_card_id: '' }));
    setCompanySearch('');
    setCompanies([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Please enter a task title');
      return;
    }

    setSaving(true);
    try {
      const url = task
        ? `${BACKEND_URL}/api/tasks/${task.task_id}`
        : `${BACKEND_URL}/api/tasks`;
      
      const response = await fetch(url, {
        method: task ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSave();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save task');
      }
    } catch (error) {
      console.error('Failed to save task:', error);
      alert('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    setFormData(prev => ({
      ...prev,
      checklist: [...prev.checklist, { item_id: `cl_${Date.now()}`, text: newChecklistItem, completed: false }]
    }));
    setNewChecklistItem('');
  };

  const removeChecklistItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.filter(item => item.item_id !== itemId)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-2xl max-h-[90vh] flex flex-col overflow-visible`}>
        <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex-shrink-0`}>
          <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {task ? 'Edit Task' : 'Create Task'}
          </h2>
          <button onClick={onClose} className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter task title..."
              className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Type</label>
              <select
                value={formData.task_type}
                onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}
                className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
              >
                {Object.entries(taskTypeLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
              >
                <option value="high">🔥 High</option>
                <option value="medium">⚡ Medium</option>
                <option value="low">🔵 Low</option>
              </select>
            </div>
          </div>

          {/* Company Association with Fuzzy Search */}
          <div>
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
              <Building2 className="w-4 h-4 inline mr-1" />
              Associate to Company
            </label>
            <div className="relative">
              <input
                type="text"
                value={companySearch}
                onChange={(e) => handleCompanySearchChange(e.target.value)}
                placeholder="Start typing company name..."
                className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none ${formData.linked_card_id ? 'pr-10' : ''}`}
              />
              {formData.linked_card_id && (
                <button
                  type="button"
                  onClick={clearCompany}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Company Search Results - Inline like BoardView */}
            {companySearch.length >= 2 && !formData.linked_card_id && (
              <div className={`mt-2 ${isDark ? 'bg-[#111] border-white/20' : 'bg-gray-50 border-gray-200'} border rounded-sm max-h-48 overflow-y-auto`}>
                {loadingCompanies ? (
                  <div className={`px-3 py-3 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Searching...
                  </div>
                ) : companies.length > 0 ? (
                  <div className="divide-y divide-white/10">
                    {companies.map(card => (
                      <button
                        key={card.card_id}
                        type="button"
                        onClick={() => selectCompany(card)}
                        className={`w-full px-3 py-2 text-left ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100 text-gray-900'} transition-colors`}
                      >
                        <span className="font-medium">{card.company_name}</span>
                        {card.column_name && (
                          <span className={`text-xs ml-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            ({card.column_name})
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className={`px-3 py-3 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    No companies found matching &quot;{companySearch}&quot;
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Assigned To</label>
              <select
                value={formData.assigned_to_id}
                onChange={(e) => setFormData({ ...formData, assigned_to_id: e.target.value })}
                className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
              >
                <option value="">Select user...</option>
                {users.map(user => (
                  <option key={user.user_id} value={user.user_id}>{user.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Due Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none flex items-center justify-between text-left`}
                    >
                      <span className={formData.due_date ? '' : (isDark ? 'text-gray-500' : 'text-gray-400')}>
                        {formData.due_date ? format(new Date(formData.due_date + 'T00:00:00'), 'dd MMM yyyy') : 'Select date'}
                      </span>
                      <CalendarIcon className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className={`w-auto p-0 ${isDark ? 'bg-[#111] border-white/20' : 'bg-white border-gray-200'}`} align="start">
                    <CalendarComponent
                      mode="single"
                      selected={formData.due_date ? new Date(formData.due_date + 'T00:00:00') : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const formattedDate = format(date, 'yyyy-MM-dd');
                          setFormData({ ...formData, due_date: formattedDate });
                        } else {
                          setFormData({ ...formData, due_date: '' });
                        }
                      }}
                      className={isDark ? 'bg-[#111] text-white' : 'bg-white text-gray-900'}
                      classNames={!isDark ? {
                        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                        month: "space-y-4",
                        caption: "flex justify-center pt-1 relative items-center text-gray-900",
                        caption_label: "text-sm font-medium text-gray-900",
                        nav: "space-x-1 flex items-center",
                        nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse space-y-1",
                        head_row: "flex",
                        head_cell: "text-gray-500 rounded-md w-8 font-normal text-[0.8rem]",
                        row: "flex w-full mt-2",
                        cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#A2FF00]/20 [&:has([aria-selected])]:rounded-md",
                        day: "h-8 w-8 p-0 font-normal text-gray-900 hover:bg-gray-100 rounded-md",
                        day_selected: "bg-[#A2FF00] text-black hover:bg-[#8EFF00] hover:text-black focus:bg-[#A2FF00] focus:text-black rounded-md",
                        day_today: "bg-gray-100 text-gray-900 font-semibold",
                        day_outside: "text-gray-400 opacity-50",
                        day_disabled: "text-gray-300 opacity-50",
                        day_hidden: "invisible",
                      } : undefined}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Time</label>
                <input
                  type="time"
                  value={formData.due_time}
                  onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
                  className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
                />
              </div>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Notes</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add notes or description..."
              rows={3}
              className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
            />
          </div>

          {formData.task_type === 'linkedin' && (
            <div className={`p-4 rounded-sm ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
              <h4 className={`text-sm font-medium ${isDark ? 'text-blue-400' : 'text-blue-700'} mb-3`}>LinkedIn Task Details</h4>
              <div className="space-y-3">
                <div>
                  <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>LinkedIn Profile URL</label>
                  <input
                    type="url"
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                    placeholder="https://linkedin.com/in/..."
                    className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
                  />
                </div>
                <div>
                  <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Message Template</label>
                  <textarea
                    value={formData.linkedin_message_template}
                    onChange={(e) => setFormData({ ...formData, linkedin_message_template: e.target.value })}
                    placeholder="Hi [Name], I noticed your work in..."
                    rows={3}
                    className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
                  />
                </div>
              </div>
            </div>
          )}

          <div>
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Checklist</label>
            <div className="space-y-2">
              {formData.checklist.map((item) => (
                <div key={item.item_id} className="flex items-center gap-2">
                  <CheckSquare className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`flex-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.text}</span>
                  <button
                    type="button"
                    onClick={() => removeChecklistItem(item.item_id)}
                    className={`${isDark ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-600'}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                  placeholder="Add checklist item..."
                  className={`flex-1 px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none text-sm`}
                />
                <button
                  type="button"
                  onClick={addChecklistItem}
                  className={`px-3 py-2 rounded-sm ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {task && (
            <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Snoozes used: {task.snooze_count}/3
            </div>
          )}

          <div className={`flex gap-3 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded-sm ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`flex-1 px-4 py-2 rounded-sm ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-bold hover:opacity-90 disabled:opacity-50`}
            >
              {saving ? 'Saving...' : task ? 'Save Changes' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main TasksPage Component
const TasksPage = () => {
  const { isDark } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ overdue_tasks: 0, today_tasks: 0, urgency_score: 0, my_tasks: 0 });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('my');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    overdue: true,
    today: true,
    upcoming: true,
    completed: false
  });
  // Bulk selection state
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [showBulkReassignModal, setShowBulkReassignModal] = useState(false);
  const [bulkReassignUserId, setBulkReassignUserId] = useState('');
  const [bulkReassigning, setBulkReassigning] = useState(false);
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTask, setEmailTask] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      let url = `${BACKEND_URL}/api/tasks?view=${view}`;
      if (filterType) url += `&task_type=${filterType}`;
      if (filterPriority) url += `&priority=${filterPriority}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [view, filterType, filterPriority]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/stats`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCurrentUserId(data.user_id);
      }
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchStats();
    fetchUsers();
    fetchCurrentUser();
  }, [fetchTasks]);

  const completeTask = async (taskId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/${taskId}/complete`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        fetchTasks();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to complete task:', error);
    }
  };

  const snoozeTask = async (taskId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/${taskId}/snooze`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        fetchTasks();
        fetchStats();
      } else {
        const error = await response.json();
        alert(error.detail);
      }
    } catch (error) {
      console.error('Failed to snooze task:', error);
    }
  };

  const retireTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to retire this task?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/${taskId}/retire`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        fetchTasks();
        fetchStats();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to retire task');
      }
    } catch (error) {
      console.error('Failed to retire task:', error);
      alert('Failed to retire task');
    }
  };

  // Bulk selection functions
  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const selectAllTasks = () => {
    const pendingTaskIds = tasks
      .filter(t => t.status === 'pending')
      .map(t => t.task_id);
    setSelectedTasks(pendingTaskIds);
  };

  const clearSelection = () => {
    setSelectedTasks([]);
  };

  const handleBulkReassign = async () => {
    if (!bulkReassignUserId) {
      alert('Please select a user to reassign tasks to');
      return;
    }
    
    setBulkReassigning(true);
    try {
      const results = await Promise.all(
        selectedTasks.map(taskId =>
          fetch(`${BACKEND_URL}/api/tasks/${taskId}/reassign`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ assigned_to_id: bulkReassignUserId })
          })
        )
      );
      
      const successCount = results.filter(r => r.ok).length;
      alert(`Successfully reassigned ${successCount} of ${selectedTasks.length} tasks`);
      
      setSelectedTasks([]);
      setShowBulkReassignModal(false);
      setBulkReassignUserId('');
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Bulk reassign error:', error);
      alert('Failed to reassign tasks');
    } finally {
      setBulkReassigning(false);
    }
  };

  // Email modal handlers
  const handleOpenEmailModal = async (task) => {
    setEmailTask(task);
    setShowEmailModal(true);
    setEmailLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-emails/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          template_key: task.email_template || 'payment_request',
          proposal_id: task.proposal_id
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailPreview(data);
      } else {
        alert('Failed to load email template');
        setShowEmailModal(false);
      }
    } catch (error) {
      console.error('Failed to load email preview:', error);
      alert('Failed to load email template');
      setShowEmailModal(false);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailPreview) return;
    
    setEmailSending(true);
    try {
      // Determine recipients to send to
      let recipientsToSend = [];
      
      if (emailPreview.all_recipients && emailPreview.all_recipients.length > 1) {
        // Multiple recipients - send to selected ones
        const selected = emailPreview.selected_recipients || emailPreview.all_recipients.map(r => r.email);
        recipientsToSend = emailPreview.all_recipients.filter(r => selected.includes(r.email));
      } else if (emailPreview.to_email) {
        // Single recipient
        recipientsToSend = [{ email: emailPreview.to_email, name: emailPreview.to_name }];
      }
      
      if (recipientsToSend.length === 0) {
        alert('Please select at least one recipient');
        setEmailSending(false);
        return;
      }
      
      // Send to each recipient
      const results = [];
      for (const recipient of recipientsToSend) {
        const response = await fetch(`${BACKEND_URL}/api/system-emails/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            to_email: recipient.email,
            to_name: recipient.name || '',
            subject: emailPreview.subject,
            body: emailPreview.body,
            template_key: emailTask?.email_template || 'payment_request',
            proposal_id: emailTask?.proposal_id
          })
        });
        
        const result = await response.json();
        results.push({ email: recipient.email, success: result.success, error: result.error });
      }
      
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      
      if (failed === 0) {
        alert(`Email sent successfully to ${successful} recipient${successful > 1 ? 's' : ''}!`);
        setShowEmailModal(false);
        setEmailTask(null);
        setEmailPreview(null);
        fetchTasks();
        fetchStats();
      } else {
        const failedEmails = results.filter(r => !r.success).map(r => `${r.email}: ${r.error}`).join('\n');
        alert(`Sent to ${successful} recipient(s). Failed: ${failed}\n\n${failedEmails}`);
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  
  const filteredTasks = tasks.filter(task => 
    task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    task.linked_card_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const overdueTasks = filteredTasks.filter(t => t.status === 'pending' && t.due_date && t.due_date < today);
  const todayTasks = filteredTasks.filter(t => t.status === 'pending' && t.due_date === today);
  const upcomingTasks = filteredTasks.filter(t => t.status === 'pending' && (!t.due_date || t.due_date > today));
  const completedTasks = filteredTasks.filter(t => t.status === 'completed');

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  if (loading) {
    return (
      <AppLayout title="Tasks">
        <div className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>Loading tasks...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Tasks">
      <div className="space-y-6">
        {/* Stats Bar - All on one line */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className={`flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>
            <button
              onClick={() => setView('my')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-sm transition-colors ${
                view === 'my' 
                  ? isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-900'
                  : 'hover:bg-white/5'
              }`}
            >
              <span className="font-medium">My Tasks</span>
              <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.my_tasks}</span>
            </button>
            <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>|</span>
            <button
              onClick={() => setView('today')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-sm transition-colors ${
                view === 'today' 
                  ? isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-900'
                  : 'hover:bg-white/5'
              }`}
            >
              <span className="font-medium">Today&apos;s Tasks</span>
              <span className={`font-bold ${stats.today_tasks > 0 ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-white' : 'text-gray-900')}`}>{stats.today_tasks}</span>
            </button>
            <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>|</span>
            <button
              onClick={() => setView('overdue')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-sm transition-colors ${
                view === 'overdue' 
                  ? isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-900'
                  : 'hover:bg-white/5'
              }`}
            >
              <span className="font-medium">Overdue</span>
              <span className={`font-bold ${stats.overdue_tasks > 0 ? 'text-red-500' : (isDark ? 'text-white' : 'text-gray-900')}`}>{stats.overdue_tasks}</span>
            </button>
            <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>|</span>
            <button
              onClick={() => setView('all')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-sm transition-colors ${
                view === 'all' 
                  ? isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-900'
                  : 'hover:bg-white/5'
              }`}
            >
              <span className="font-medium">All Tasks</span>
            </button>
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-bold rounded-sm hover:opacity-90`}
          >
            <Plus className="w-4 h-4" />
            New Task
          </button>
        </div>

        <div className={`flex flex-wrap items-center gap-4 p-4 rounded-sm ${isDark ? 'bg-[#111] border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
          <div className="flex-1 min-w-[200px] relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
            />
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className={`px-4 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
          >
            <option value="">All Types</option>
            {Object.entries(taskTypeLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className={`px-4 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
          >
            <option value="">All Priorities</option>
            <option value="high">🔥 High</option>
            <option value="medium">⚡ Medium</option>
            <option value="low">🔵 Low</option>
          </select>
        </div>

        {/* Bulk Actions Bar */}
        <div className={`flex items-center gap-4 ${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-100 border-gray-200'} border rounded-sm p-3`}>
          <div className="flex items-center gap-2">
            <button
              onClick={() => selectedTasks.length > 0 ? clearSelection() : selectAllTasks()}
              className={`px-3 py-1.5 rounded-sm text-sm font-medium ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              {selectedTasks.length > 0 ? 'Clear Selection' : 'Select All'}
            </button>
            {selectedTasks.length > 0 && (
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {selectedTasks.length} selected
              </span>
            )}
          </div>
          
          {selectedTasks.length > 0 && (
            <button
              onClick={() => setShowBulkReassignModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white text-sm font-bold rounded-sm hover:bg-blue-600 transition-colors"
            >
              <Users className="w-4 h-4" />
              Reassign Selected
            </button>
          )}
        </div>

        <div>
          <TaskSection 
            title="OVERDUE" 
            tasks={overdueTasks} 
            color="bg-red-500" 
            sectionKey="overdue"
            expanded={expandedSections.overdue}
            onToggle={() => toggleSection('overdue')}
            isDark={isDark}
            today={today}
            onComplete={completeTask}
            onSnooze={snoozeTask}
            onEdit={setEditingTask}
            onRetire={retireTask}
            onSendEmail={handleOpenEmailModal}
            selectedTasks={selectedTasks}
            onToggleSelect={toggleTaskSelection}
          />
          <TaskSection 
            title="TODAY" 
            tasks={todayTasks} 
            color="bg-green-500" 
            sectionKey="today"
            expanded={expandedSections.today}
            onToggle={() => toggleSection('today')}
            isDark={isDark}
            today={today}
            onComplete={completeTask}
            onSnooze={snoozeTask}
            onEdit={setEditingTask}
            onRetire={retireTask}
            onSendEmail={handleOpenEmailModal}
            selectedTasks={selectedTasks}
            onToggleSelect={toggleTaskSelection}
          />
          <TaskSection 
            title="UPCOMING" 
            tasks={upcomingTasks} 
            color="bg-blue-500" 
            sectionKey="upcoming"
            expanded={expandedSections.upcoming}
            onToggle={() => toggleSection('upcoming')}
            isDark={isDark}
            today={today}
            onComplete={completeTask}
            onSnooze={snoozeTask}
            onEdit={setEditingTask}
            onRetire={retireTask}
            onSendEmail={handleOpenEmailModal}
            selectedTasks={selectedTasks}
            onToggleSelect={toggleTaskSelection}
          />
          <TaskSection 
            title="COMPLETED" 
            tasks={completedTasks} 
            color="bg-gray-500" 
            sectionKey="completed"
            expanded={expandedSections.completed}
            onToggle={() => toggleSection('completed')}
            isDark={isDark}
            today={today}
            onComplete={completeTask}
            onSnooze={snoozeTask}
            onEdit={setEditingTask}
            onRetire={retireTask}
            onSendEmail={handleOpenEmailModal}
            selectedTasks={selectedTasks}
            onToggleSelect={toggleTaskSelection}
          />
          
          {filteredTasks.length === 0 && (
            <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <CheckSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No tasks found</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className={`mt-4 px-4 py-2 ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} rounded-sm`}
              >
                Create your first task
              </button>
            </div>
          )}
        </div>
      </div>

      {(showCreateModal || editingTask) && (
        <TaskModal
          task={editingTask}
          users={users}
          currentUserId={currentUserId}
          isDark={isDark}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTask(null);
          }}
          onSave={() => {
            fetchTasks();
            fetchStats();
            setShowCreateModal(false);
            setEditingTask(null);
          }}
        />
      )}

      {/* Bulk Reassign Modal */}
      {showBulkReassignModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowBulkReassignModal(false)}>
          <div 
            className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-md`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Reassign {selectedTasks.length} Task{selectedTasks.length !== 1 ? 's' : ''}
              </h3>
              <button onClick={() => setShowBulkReassignModal(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                  Assign tasks to:
                </label>
                <select
                  value={bulkReassignUserId}
                  onChange={(e) => setBulkReassignUserId(e.target.value)}
                  className={`w-full px-3 py-2 rounded-sm ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] outline-none`}
                >
                  <option value="">Select a user...</option>
                  {users.map(user => (
                    <option key={user.user_id} value={user.user_id}>{user.name}</option>
                  ))}
                </select>
              </div>
              
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                This will reassign all selected tasks to the chosen user. This action is useful for holiday coverage or team changes.
              </p>
            </div>
            
            <div className={`flex gap-3 p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowBulkReassignModal(false)}
                className={`flex-1 px-4 py-2 rounded-sm ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkReassign}
                disabled={bulkReassigning || !bulkReassignUserId}
                className="flex-1 px-4 py-2 rounded-sm bg-blue-500 text-white font-bold hover:bg-blue-600 disabled:opacity-50"
              >
                {bulkReassigning ? 'Reassigning...' : 'Reassign Tasks'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview/Send Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-2xl max-h-[80vh] overflow-hidden`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Send Payment Request Email
              </h3>
              <button
                onClick={() => { setShowEmailModal(false); setEmailTask(null); setEmailPreview(null); }}
                className={`p-2 ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'} rounded-sm`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {emailLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className={`w-8 h-8 border-2 ${isDark ? 'border-[#A2FF00]' : 'border-emerald-600'} border-t-transparent rounded-full animate-spin`} />
                </div>
              ) : emailPreview ? (
                <div className="space-y-4">
                  {/* Recipients */}
                  {emailPreview.all_recipients && emailPreview.all_recipients.length > 1 ? (
                    <div>
                      <label className={`block text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-2`}>
                        Recipients ({emailPreview.all_recipients.length}):
                      </label>
                      <div className="space-y-2">
                        {emailPreview.all_recipients.map((recipient, idx) => (
                          <label key={idx} className={`flex items-center gap-3 p-2 rounded-sm cursor-pointer ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'}`}>
                            <input
                              type="checkbox"
                              checked={emailPreview.selected_recipients?.includes(recipient.email) ?? true}
                              onChange={(e) => {
                                const selected = emailPreview.selected_recipients || emailPreview.all_recipients.map(r => r.email);
                                if (e.target.checked) {
                                  setEmailPreview({ ...emailPreview, selected_recipients: [...selected, recipient.email] });
                                } else {
                                  setEmailPreview({ ...emailPreview, selected_recipients: selected.filter(e => e !== recipient.email) });
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-[#A2FF00] focus:ring-[#A2FF00]"
                            />
                            <div className="flex-1">
                              <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{recipient.name || 'No name'}</span>
                              <span className={`ml-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{recipient.email}</span>
                              {recipient.role && (
                                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                                  {recipient.role.replace('_', ' ')}
                                </span>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>To:</label>
                        <input
                          type="email"
                          value={emailPreview.to_email}
                          onChange={(e) => setEmailPreview({ ...emailPreview, to_email: e.target.value })}
                          className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm p-2 text-sm`}
                          placeholder="recipient@email.com"
                        />
                      </div>
                      <div>
                        <label className={`block text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>Recipient Name:</label>
                        <input
                          type="text"
                          value={emailPreview.to_name}
                          onChange={(e) => setEmailPreview({ ...emailPreview, to_name: e.target.value })}
                          className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm p-2 text-sm`}
                          placeholder="John Smith"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Subject */}
                  <div>
                    <label className={`block text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>Subject:</label>
                    <input
                      type="text"
                      value={emailPreview.subject}
                      onChange={(e) => setEmailPreview({ ...emailPreview, subject: e.target.value })}
                      className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm p-2 text-sm`}
                    />
                  </div>
                  
                  {/* Body */}
                  <div>
                    <label className={`block text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>Email Body:</label>
                    <div 
                      className={`${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm p-3 text-sm prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''} min-h-[200px]`}
                      contentEditable
                      dangerouslySetInnerHTML={{ __html: sanitizeHTML(emailPreview.body) }}
                      onBlur={(e) => setEmailPreview({ ...emailPreview, body: e.target.innerHTML })}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Failed to load email template
                </div>
              )}
            </div>
            
            <div className={`flex items-center justify-end gap-3 p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <button
                onClick={() => { setShowEmailModal(false); setEmailTask(null); setEmailPreview(null); }}
                className={`px-4 py-2 ${isDark ? 'border-white/20 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} border rounded-sm`}
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={emailSending || !emailPreview?.to_email}
                className={`px-4 py-2 rounded-sm font-medium flex items-center gap-2 ${
                  emailPreview?.to_email && !emailSending
                    ? 'bg-[#A2FF00] text-black hover:bg-[#8FE500]'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                {emailSending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default TasksPage;
